import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authService } from '../services/authService';
import { tryRefreshSession } from '../services/api';
import type { User, LoginCredentials, RegisterData } from '../types/auth';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 初期化時にセッションを復元する。
  //
  // アクセストークンは15分で切れるため、「トークンが無い / 期限切れ」は通常の状態。
  // リフレッシュトークン(httpOnly Cookie)が生きていればサーバーから再発行できるので、
  // ここで復元を試みる。これがユーザーから見た「ログインしっぱなし」の実体。
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { token } = authService.getAuthData();

        if (token) {
          try {
            setUser(await authService.getCurrentUser());
            return;
          } catch {
            // 期限切れの可能性 → 下のリフレッシュへ進む
          }
        }

        // Cookie が残っていればここでセッションが蘇る（残っていなければ未ログインのまま）
        const refreshed = await tryRefreshSession();
        if (refreshed) {
          setUser(await authService.getCurrentUser());
        } else {
          authService.clearAuthData();
        }
      } catch (error) {
        console.error('認証初期化エラー:', error);
        authService.clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      authService.saveAuthData(response.token, response.user);
      setUser(response.user);
    } catch (error) {
      console.error('ログインエラー:', error);
      // ログイン失敗時は認証データをクリアしてリダイレクトを防ぐ
      authService.clearAuthData();
      setUser(null);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response = await authService.register(data);
      authService.saveAuthData(response.token, response.user);
      setUser(response.user);
    } catch (error) {
      console.error('登録エラー:', error);
      throw error;
    }
  };

  const logout = () => {
    // サーバー側のリフレッシュトークンも失効させる。
    // これをしないと Cookie が生き残り、次回アクセスでセッションが蘇ってしまう。
    authService.logout().catch((error) => {
      console.error('ログアウト通知エラー:', error);
    });
    authService.clearAuthData();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      authService.saveAuthData(authService.getAuthData().token!, currentUser);
    } catch (error) {
      console.error('ユーザー情報更新エラー:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};